// Maps a dashboard chart spec onto props for the shared ChartRenderer.
import type { ChartRendererProps } from '@/componentsV1/Charts/types';
import { colors } from '@/theme/foundations/colors';
import type { DashboardChart } from '../../../types';
import { formatAmount } from './helpers';

// White so the chart matches its card (ChartRenderer defaults to transparent).
const WHITE = colors['base-white'];

// Highcharts wants a colour string, not a CSS var.
const withAlpha = (hex: string, alpha: number): string => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const BAND = {
  series: colors['primary-500'],
  seriesFill: withAlpha(colors['primary-500'], 0.25),
  median: colors['primary-900'],
  outer: colors['secondary-400'],
  current: colors['status-3'],
  recommended: colors['pastel-2-dark'],
  recommendedFill: withAlpha(colors['pastel-2-dark'], 0.25),
};

type RendererProps = Pick<
  ChartRendererProps,
  'chartType' | 'height' | 'data' | 'highchartsOptions'
>;

// Percentile range: a columnrange IQR band + scatter markers (median, P90) +
// optional current/recommended overlays.
const rangeProps = (chart: DashboardChart): RendererProps => {
  const bands = chart.ranges ?? [];
  const fmt = (n: number) => formatAmount(n, chart.numberFormat);
  const unitPrefix = chart.unit ? `${chart.unit} ` : '';

  const series: Record<string, unknown>[] = [
    {
      type: 'columnrange',
      name: 'P25–P75',
      data: bands.map((b) => [b.p25, b.p75]),
      color: BAND.seriesFill,
      borderColor: BAND.series,
      borderWidth: 1,
      pointWidth: 26,
    },
    {
      type: 'scatter',
      name: 'Median (P50)',
      data: bands.map((b, i) => ({ x: i, y: b.p50 })),
      color: BAND.median,
      marker: { symbol: 'diamond', radius: 6 },
    },
    {
      type: 'scatter',
      name: 'P90',
      data: bands.map((b, i) => ({ x: i, y: b.p90 })),
      color: BAND.outer,
      marker: { symbol: 'circle', radius: 4 },
    },
  ];

  const currents = bands
    .map((b, i) =>
      b.currentMax ? { x: i, low: b.currentMin ?? 0, high: b.currentMax } : null
    )
    .filter(Boolean);
  if (currents.length > 0) {
    series.push({
      type: 'columnrange',
      name: 'Current band',
      data: currents,
      color: 'transparent',
      borderColor: BAND.current,
      borderWidth: 2,
      dashStyle: 'Dash',
      pointWidth: 34,
    });
  }
  const recs = bands
    .map((b, i) =>
      b.recMax ? { x: i, low: b.recMin ?? 0, high: b.recMax } : null
    )
    .filter(Boolean);
  if (recs.length > 0) {
    series.push({
      type: 'columnrange',
      name: 'Recommended',
      data: recs,
      color: BAND.recommendedFill,
      borderColor: BAND.recommended,
      borderWidth: 2,
      pointWidth: 18,
    });
  }

  return {
    // Scatter base; each series keeps its own type (columnrange/scatter) via
    // ChartRenderer's pass-through, and highcharts-more is already registered.
    chartType: 'scatter',
    height: `${120 + bands.length * 60}px`,
    data: { categories: bands.map((b) => b.label), series },
    highchartsOptions: {
      // `type` repeated: ChartRenderer spreads highchartsOptions last, so a
      // chart override without it drops the computed type (→ line).
      chart: { type: 'scatter', inverted: true, backgroundColor: WHITE },
      legend: { enabled: true },
      // Reset the inherited column stacking so the bands sit independently.
      plotOptions: { columnrange: { grouping: false, stacking: undefined } },
      yAxis: {
        title: { text: chart.unit ?? null },
        labels: {
          formatter(this: { value: number | string }) {
            return typeof this.value === 'number'
              ? fmt(this.value)
              : String(this.value);
          },
        },
      },
      // Name the value; the default scatter tooltip reads "x: 0 · y: 225000".
      tooltip: {
        shared: false,
        formatter(this: {
          series: { name: string };
          point: { low?: number; high?: number; y?: number };
        }) {
          const one = (n?: number) =>
            typeof n === 'number' ? unitPrefix + fmt(n) : '';
          if (typeof this.point.high === 'number') {
            return `<b>${this.series.name}</b>: ${one(this.point.low)} – ${one(this.point.high)}`;
          }
          return `<b>${this.series.name}</b>: ${one(this.point.y)}`;
        },
      },
      exporting: { enabled: false },
    },
  };
};

// bar / column / stacked_bar / donut → ChartRenderer's native types.
export const toChartRendererProps = (chart: DashboardChart): RendererProps => {
  if (chart.chartType === 'range') return rangeProps(chart);

  const categories = chart.categories ?? [];
  const specSeries = chart.series ?? [];

  if (chart.chartType === 'donut') {
    return {
      chartType: 'pie',
      height: '240px',
      data: {
        series: categories.map((c, i) => ({
          name: c,
          y: specSeries[0]?.data?.[i] ?? 0,
        })),
      },
      highchartsOptions: {
        chart: { type: 'pie', backgroundColor: WHITE },
        exporting: { enabled: false },
      },
    };
  }

  const isStacked = chart.chartType === 'stacked_bar';
  const hcType = isStacked ? 'bar' : chart.chartType;
  return {
    chartType: hcType,
    height: '220px',
    data: {
      categories,
      series: specSeries.map((s) => ({ name: s.name, data: s.data })),
    },
    highchartsOptions: {
      chart: { type: hcType, backgroundColor: WHITE },
      yAxis: { title: { text: chart.unit ?? null } },
      legend: { enabled: specSeries.length > 1 },
      ...(isStacked ? { plotOptions: { series: { stacking: 'normal' } } } : {}),
      exporting: { enabled: false },
    },
  };
};
